import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
	Box,
	TextField,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Button,
	FormHelperText,
	Paper,
} from '@mui/material';
import { amfiFormStyles } from './styles';

/**
 * Form component for creating and editing AMFI items
 * Currently focused on attributes, will be expanded for other types
 */
const AMFIForm = ({
	type = 'attribute', // Currently only handling 'attribute'
	item = null, // If provided, we're in edit mode
	categories = [],
	onSubmit,
	onCancel,
}) => {
	// Form state
	const [formState, setFormState] = useState({
		name: '',
		metric_name: '', // For metrics
		category: '',
		value: '',
		content: '', // For facts and insights
		unit: '',
		source: '',
		notes: '',
		is_key: false,
	});

	// Error state
	const [errors, setErrors] = useState({});

	// Detect if in edit mode and populate form
	useEffect(() => {
		if (item) {
			// Handle different field names for metrics vs attributes
			const nameValue =
				type === 'metric' ? item.metric_name || '' : item.name || '';

			setFormState({
				name: nameValue,
				metric_name: nameValue, // Use the same value for both fields initially
				category: item.category || '',
				value: item.value || '',
				content: item.content || '',
				unit: item.unit || '',
				source: item.source || '',
				notes: item.notes || '',
				is_key: item.is_key || false,
			});
		}
	}, [item, type]);

	// Handle form input changes
	const handleChange = (e) => {
		const { name, value, checked, type } = e.target;
		setFormState((prev) => ({
			...prev,
			[name]: type === 'checkbox' ? checked : value,
		}));

		// Clear error for this field if it exists
		if (errors[name]) {
			setErrors((prev) => ({
				...prev,
				[name]: '',
			}));
		}
	};

	// Validate the form
	const validateForm = () => {
		const newErrors = {};

		// Required fields - different name field based on type
		if (type === 'metric') {
			if (!formState.metric_name.trim()) {
				newErrors.metric_name = 'Metric name is required';
			}
		} else if (type === 'attribute') {
			// Only require name for attributes, not for facts or insights
			if (!formState.name.trim()) {
				newErrors.name = 'Name is required';
			}
		}

		// Category is now optional
		// Removed validation for category

		// Validate value for attributes and metrics
		if (type === 'attribute' || type === 'metric') {
			if (!formState.value && formState.value !== 0) {
				newErrors.value = 'Value is required';
			}
		}

		// Validate content for facts and insights
		if (type === 'fact' || type === 'insight') {
			if (!formState.content.trim()) {
				newErrors.content = 'Content is required';
			}
		}

		// Metric-specific validation
		if (type === 'metric') {
			// Unit is now optional, so no validation for unit field

			// Check that value is numeric for metrics
			if (formState.value && isNaN(parseFloat(formState.value))) {
				newErrors.value = 'Metric value must be a number';
			}
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	// Handle form submission
	const handleSubmit = (e) => {
		e.preventDefault();

		if (validateForm()) {
			// Create data object to submit with the correct field names
			let data;

			if (type === 'metric') {
				// For metrics, use metric_name instead of name
				const { name: _, ...rest } = formState;
				data = { ...rest };
			} else {
				// For attributes and others, use name and exclude metric_name
				const { metric_name: _, ...rest } = formState;
				data = { ...rest };
			}

			onSubmit(data, item ? 'edit' : 'create');
		}
	};

	return (
		<Paper elevation={0}>
			<Box component='form' onSubmit={handleSubmit} sx={amfiFormStyles.form}>

				{/* Name field - different field name for metrics, not shown for facts */}
				{type === 'metric' ? (
					<TextField
						fullWidth
						label='Metric Name'
						name='metric_name'
						value={formState.metric_name}
						onChange={handleChange}
						error={!!errors.metric_name}
						helperText={errors.metric_name}
						margin='normal'
						required
					/>
				) : type === 'attribute' ? (
					<TextField
						fullWidth
						label='Name'
						name='name'
						value={formState.name}
						onChange={handleChange}
						error={!!errors.name}
						helperText={errors.name}
						margin='normal'
						required
					/>
				) : null}

				{/* Value field - only for attributes and metrics */}
				{(type === 'attribute' || type === 'metric') && (
					<TextField
						fullWidth
						label='Value'
						name='value'
						value={formState.value}
						onChange={handleChange}
						error={!!errors.value}
						helperText={errors.value}
						margin='normal'
						required
						type={type === 'metric' ? 'number' : 'text'}
					/>
				)}

				{/* Content field - for facts and insights */}
				{(type === 'fact' || type === 'insight') && (
					<TextField
						fullWidth
						label='Content'
						name='content'
						value={formState.content}
						onChange={handleChange}
						error={!!errors.content}
						helperText={errors.content}
						margin='normal'
						required
						multiline
						rows={4}
					/>
				)}

				{/* Category dropdown */}
				<FormControl fullWidth margin='normal' error={!!errors.category}>
					<InputLabel id='category-label'>Category</InputLabel>
					<Select
						labelId='category-label'
						name='category'
						value={formState.category}
						onChange={handleChange}
						label='Category'
					>
						{categories.map((category) => (
							<MenuItem
								key={category.id || category}
								value={category.id || category}
							>
								{category.display_name || category}
							</MenuItem>
						))}
					</Select>
					{errors.category && (
						<FormHelperText>{errors.category}</FormHelperText>
					)}
				</FormControl>

				{/* Unit field - only for metrics */}
				{type === 'metric' && (
					<FormControl fullWidth margin='normal'>
						<InputLabel id='unit-label'>Unit</InputLabel>
						<Select
							labelId='unit-label'
							name='unit'
							value={formState.unit}
							onChange={handleChange}
						>
							<MenuItem value=''>None</MenuItem>
							<MenuItem value='USD'>USD ($)</MenuItem>
							<MenuItem value='percentage'>Percentage (%)</MenuItem>
							<MenuItem value='count'>Count (#)</MenuItem>
							<MenuItem value='years'>Years (yr)</MenuItem>
							<MenuItem value='months'>Months (mo)</MenuItem>
						</Select>
					</FormControl>
				)}

				{/* Source field (optional) */}
				<TextField
					fullWidth
					label='Source'
					name='source'
					value={formState.source}
					onChange={handleChange}
					margin='normal'
				/>

				{/* Notes field (optional) */}
				<TextField
					fullWidth
					label='Notes'
					name='notes'
					value={formState.notes}
					onChange={handleChange}
					margin='normal'
					multiline
					rows={3}
				/>

				{/* Is Key checkbox */}
				<Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
					<input
						type='checkbox'
						id='is_key'
						name='is_key'
						checked={formState.is_key}
						onChange={handleChange}
						style={{ marginRight: '8px' }}
					/>
					<label htmlFor='is_key'>Mark as key attribute</label>
				</Box>

				{/* Form actions */}
				<Box sx={amfiFormStyles.formActions}>
					<Button onClick={onCancel} variant='outlined' sx={{ mr: 1 }}>
						Cancel
					</Button>
					<Button type='submit' variant='contained' color='primary'>
						{item ? 'Update' : 'Create'}
					</Button>
				</Box>
			</Box>
		</Paper>
	);
};

AMFIForm.propTypes = {
	type: PropTypes.oneOf(['attribute', 'metric', 'fact', 'insight']),
	item: PropTypes.object, // The item to edit, null for create mode
	categories: PropTypes.arrayOf(
		PropTypes.oneOfType([
			PropTypes.string,
			PropTypes.shape({
				id: PropTypes.string,
				display_name: PropTypes.string,
			}),
		])
	),
	onSubmit: PropTypes.func.isRequired,
	onCancel: PropTypes.func.isRequired,
};

export default AMFIForm;
